// src/index.ts
import { z } from "zod";
var defineVersion = (def) => def;
var VersionedEntity = class {
  /**
   * @package
   */
  constructor(versionMap, latestVersion, getVersion) {
    this.versionMap = versionMap;
    this.latestVersion = latestVersion;
    this.getVersion = getVersion;
  }
  /**
   * Returns whether the given data is a valid entity of any version of the entity.
   * @param data The data to check
   * @returns Whether the given data is a valid entity of any version of the entity.
   */
  is(data) {
    let ver = this.getVersion(data);
    if (ver === null)
      return false;
    const verDef = this.versionMap[ver];
    if (!verDef)
      return false;
    return verDef.schema.safeParse(data).success;
  }
  /**
   * Returns whether the given data is a valid entity of the latest version of the entity.
   * @param data The data to check
   * @returns Whether the given data is a valid entity of the latest version of the entity.
   */
  isLatest(data) {
    return this.versionMap[this.latestVersion].schema.safeParse(data).success;
  }
  /**
   * Similar to Zod's `safeParse` method, but also migrates the data to the latest version.
   * @param data The data to parse
   * @returns The result from parsing data, if successful, older versions are migrated to the latest version
   */
  safeParse(data) {
    const ver = this.getVersion(data);
    if (ver === null) {
      return { type: "err", error: { type: "VER_CHECK_FAIL" } };
    }
    const verDef = this.versionMap[ver];
    if (!verDef) {
      return { type: "err", error: { type: "INVALID_VER" } };
    }
    const pass = verDef.schema.safeParse(data);
    if (!pass.success) {
      return {
        type: "err",
        error: {
          type: "GIVEN_VER_VALIDATION_FAIL",
          version: ver,
          versionDef: verDef,
          error: pass.error
        }
      };
    }
    let finalData = data;
    for (let up = ver + 1; up <= this.latestVersion; up++) {
      const upDef = this.versionMap[up];
      if (!upDef) {
        return {
          type: "err",
          error: { type: "BUG_NO_INTERMEDIATE_FOUND", missingVer: up }
        };
      }
      if (upDef.initial) {
        return {
          type: "err",
          error: { type: "BUG_INTERMEDIATE_MARKED_INITIAL", ver: up }
        };
      }
      finalData = upDef.up(finalData);
    }
    return { type: "ok", value: finalData };
  }
};
function createVersionedEntity(def) {
  return new VersionedEntity(def.versionMap, def.latestVersion, def.getVersion);
}
function entityReference(entity) {
  return z.custom((data) => {
    return entity.is(data);
  }).transform((data) => {
    const parseResult = entity.safeParse(data);
    if (parseResult.type !== "ok") {
      throw new Error(
        "Invalid entity definition. `entity.is` returned success, safeParse failed."
      );
    }
    return parseResult.value;
  });
}
export {
  VersionedEntity,
  createVersionedEntity,
  defineVersion,
  entityReference
};
//# sourceMappingURL=index.js.map